<template>
	<div class="task-group" :class="{ 'task-group--expanded' : expanded, 'task-group--show-actions-button' : showActionsButton }">
		<div class="task-group__header relative flex items-center mb-4 gap-4 min-h-20">
			<button
				class="task-group__header-button button button--no-style flex items-center pr-3"
				:class="{ 'task-group__header-button--expanded' : expanded }"
				data-testid="chevron-down"
				@click="expanded = !expanded"
			>
				<div class="task-group__header-button-chevron flex items-center">
					<Inline
						:intense="false"
						svg="chevron-down"
						:class="{ '-rotate-90' : !expanded }"
					/>
				</div>
				<h2 class="sz-4 truncate">{{ localizeGroupName }}</h2>
				<CBadge :text="taskCount" />
			</button>
			<Transition name="fade" :duration="100">
				<div v-if="showActionsButton" class="task-group__action-button ml-auto shrink-0">
					<CMultiButton
						v-if="actions.length > 1 || !allActionsLoaded"
						:buttons="actions"
						:button-status="buttonStatus"
						:disabled="buttonStatus === 'validating'"
						@click="onActionClick"
						@dropdown-click="onDropdownClick"
					>
						<CBadge :text="taskCount" type="translucent" class="ml-2" />
					</CMultiButton>
					<CStatusButton
						v-else-if="actions.length === 1 && allActionsLoaded"
						class="status-button button button--primary"
						:status="buttonStatus"
						@click.native="onActionClick(actions.at(0).value)"
					>
						<template #icon2><CBadge :text="taskCount" type="translucent" class="ml-2" /></template>
						{{ actions.at(0).displayValue }}
					</CStatusButton>
				</div>
			</Transition>
		</div>
		<CExpandable :expanded="expanded">
			<slot v-if="expanded" />
		</CExpandable>
	</div>
</template>

<script lang="ts">
	import '@cognitoforms/styles/global/_motion.scss';
	import type { PropType } from 'vue';
	import type { DisplayAction } from './utils/use-task-actions';
	import { computed, defineComponent, inject, ref, watch } from 'vue';
	import { GroupingOptions, TaskOrganizationPriority, TaskOrganizationDate } from './utils/use-task-management';
	import CBadge from 'src/components/info/Badge.vue';
	import CExpandable from 'src/components/interaction/Expandable.vue';
	import CMultiButton from 'src/components/form-controls/MultiButton.vue';
	import CStatusButton from 'src/components/interaction/StatusButton.vue';
	import type { ButtonStatus } from 'src/components/interaction/StatusButton.vue';
	import { localizationResourcesKey } from 'src/utilities/injection-keys';

	export default defineComponent({
		components: {
			CBadge,
			CExpandable,
			CMultiButton,
			CStatusButton
		},
		props: {
			selectedGrouping: { type: String as PropType<GroupingOptions>, required: true },
			viewId: { type: String, default: null },
			groupName: { type: String, required: true },
			taskCount: { type: Number, required: true },
			actions: { type: Array as PropType<DisplayAction[]>, default: () => [] },
			allActionsLoaded: { type: Boolean, default: false },
			numberOfGroups: { type: Number, default: null },
			isActionInProgress: { type: Boolean, default: false },
			isGuest: { type: Boolean, default: false }
		},
		emits: ['perform-action', 'load-all-actions'],
		setup(props, { emit }) {
			const resources = inject(localizationResourcesKey);

			const localizationMap: Record<TaskOrganizationPriority | TaskOrganizationDate, string> = {
				[TaskOrganizationPriority.High]: resources.value.HighPriority,
				[TaskOrganizationPriority.Medium]: resources.value.MediumPriority,
				[TaskOrganizationPriority.Low]: resources.value.LowPriority,
				[TaskOrganizationPriority.Unprioritized]: resources.value.Unprioritized,
				[TaskOrganizationDate.PastDue]: resources.value.PastDue,
				[TaskOrganizationDate.Today]: resources.value.Today,
				[TaskOrganizationDate.Tomorrow]: resources.value.Tomorrow,
				[TaskOrganizationDate.ThisWeek]: resources.value.ThisWeek,
				[TaskOrganizationDate.NextWeek]: resources.value.NextWeek,
				[TaskOrganizationDate.Later]: resources.value.Later,
				[TaskOrganizationDate.Unscheduled]: resources.value.Unscheduled
			};

			const localizeGroupName = computed(() => {
				return localizationMap[props.groupName] ?? props.groupName;
			});

			const buttonStatus = ref<ButtonStatus>('default');
			watch(() => props.allActionsLoaded, (allActionsLoaded) => {
				if (allActionsLoaded) {
					buttonStatus.value = 'default';
				}
			});

			const expanded = ref(true);
			const showActionsButton = computed(() => {
				return !props.isGuest && props.selectedGrouping === 'Type' && props.actions.length > 0;
			});

			const onActionClick = (actionName) => {
				if (props.isActionInProgress) {
					buttonStatus.value = 'error';
					return;
				}

				const eventData = {
					viewId: props.viewId,
					actionName: actionName
				};
				emit('perform-action', eventData);
			};

			const onDropdownClick = () => {
				if (!props.allActionsLoaded) {
					buttonStatus.value = 'validating';
					emit('load-all-actions', props.viewId);
				}
			};

			const isMultipleGroups = props.numberOfGroups > 1;
			const isUnprioritized = props.selectedGrouping === GroupingOptions.Priority && props.groupName === TaskOrganizationPriority.Unprioritized;
			const isUnscheduled = props.selectedGrouping === GroupingOptions.DueDate && props.groupName === TaskOrganizationDate.Unscheduled;

			if (isMultipleGroups && (isUnprioritized || isUnscheduled))
				expanded.value = false;

			return {
				localizeGroupName,
				buttonStatus,
				expanded,
				showActionsButton,
				onActionClick,
				onDropdownClick
			};
		}
	});
</script>

<style lang="scss">
.task-group {
	height: fit-content;
	container: task-group / inline-size;
	transition: margin-top $speed__hovered;

	$chevron-button-space: $size__icon + $space__1 + $space__4;

	&__header {
		width: calc(100% + $chevron-button-space);

		// Prevent shifting when showing/hiding bulk action button
		min-height: 50px;
		margin-left: calc($chevron-button-space * -1);

		&-button {
			min-width: 0;
			color: var(--shade-9);
			transition: $speed__hovered;

			&-chevron {
				border-radius: 50%;
				margin-right: $space__1;
				padding: $space__3;
				color: var(--icon-subtle);
				transition: $speed__hovered;
			}

			.badge {
				margin-left: $space__4;
			}

			.admin--desktop &:hover {
				color: var(--text-primary);

				.task-group__header-button-chevron {
					background-color: var(--bg-hovered);
				}
			}

			&--expanded {
				color: var(--text-primary);

				.admin--desktop &:hover {
					color: var(--shade-9);
				}
			}
		}

		/* stylelint-disable-next-line scss/at-rule-no-unknown */
		@container task-dashboard (max-width: #{1500px + $chevron-button-space + ($space__4 * 3)}) {
			width: calc(100% + $space__3);
			margin-left: calc($space__3 * -1);

			// Line up with the My Tasks header
			.is-mobile &-button-chevron {
				margin-right: 6px;
			}
		}
	}

	/* stylelint-disable-next-line scss/at-rule-no-unknown */
	@container task-dashboard (max-width: 800px) {

		&--show-actions-button {

			.task-group__header-button {

				.badge {
					display: none;
				}
			}
		}
	}

	.multi-button {

		.el-button {
			font-family: $app-text-type;
		}
	}

	&__action-button {

		.status-button {
			display: flex;
			align-items: center;
			max-height: 31.5px;
		}
	}

	& + & {
		margin-top: $space__1;
	}

	&--expanded + & {
		margin-top: $space__6;
	}
}
</style>